<svelte:options runes={false} />

<script>
  import { isInvoicePayable } from '../../api/invoices';
  import { invoiceStatusTone, statusLabel } from '../status-labels.mjs';
  import ExpectedQuotePanel from './ExpectedQuotePanel.svelte';
  import InvoiceDraftEditor from './InvoiceDraftEditor.svelte';
  import InvoiceLifecycleActions from './InvoiceLifecycleActions.svelte';
  import NoticeBanner from './NoticeBanner.svelte';
  import OperatorHourBitCard from './OperatorHourBitCard.svelte';
  import PaymentAttemptCard from './PaymentAttemptCard.svelte';
  import SectionHeading from './SectionHeading.svelte';
  import ShareInvoiceLinkPanel from './ShareInvoiceLinkPanel.svelte';
  import StatusPill from './StatusPill.svelte';

  export let invoice;
  export let detailReady = true;
  export let isOperator;
  export let instanceMode;
  export let showArchived;
  export let hasNoInvoicesHere;
  export let invoicePage;
  export let creatingInvoice;
  export let canCreateInvoice;
  export let onCreateInvoice;
  export let invoiceIssuanceIssues;
  export let invoiceReadinessWarnings;
  export let operatorExpectedQuote;
  export let operatorFeeQuoteAvailable;
  export let draftTitle;
  export let draftDueDate;
  export let draftTaxPercent;
  export let draftCurrency;
  export let draftMinimumRate;
  export let savingInvoiceDraft;
  export let editingHourBitId;
  export let bitLabel;
  export let bitHours;
  export let bitFixedCost;
  export let bitDescription;
  export let bitReported;
  export let savingHourBit;
  export let payerUrl;
  export let hourBits;
  export let bitRates;
  export let attempts;
  export let attemptItems;
  export let riskObservations;
  export let onSetInvoiceStatus;
  export let onSetArchived;
  export let onSaveDraft;
  export let onSaveBit;
  export let onCancelEditBit;
  export let onEditBit;
  export let onRemoveBit;
  export let onToggleReported;
  export let onCopyLink;
  export let onShareLink;
  export let onBackToList;

  $: invoiceAttempts = invoice
    ? attempts.filter((attempt) => attempt.invoiceId === invoice._id)
    : [];
</script>

<section class="panel operator-detail-panel">
  {#if invoice && !detailReady}
    {#if isOperator}
      <button class="mobile-only" type="button" on:click={onBackToList}>‹ Back to invoices</button>
    {/if}
    <p class="quiet">Loading invoice details…</p>
  {:else if invoice}
    {#if isOperator}
      <button class="mobile-only" type="button" on:click={onBackToList}>‹ Back to invoices</button>
    {/if}
    <!-- One heading, not two near-identical copies differing only in whether the
         lifecycle buttons hang off it. -->
    <SectionHeading eyebrow={invoice.invoiceReference || invoice.status} title={invoice.recipientLabel}>
      <p class="quiet"><StatusPill label={statusLabel(invoice.status)} tone={invoiceStatusTone(invoice.status)} /></p>
      <svelte:fragment slot="trailing">
        {#if isOperator}
          <InvoiceLifecycleActions
            status={invoice.status}
            archivedAt={invoice.archivedAt}
            {instanceMode}
            issuanceBlocked={invoiceIssuanceIssues.length > 0}
            onIssue={() => onSetInvoiceStatus('issued')}
            onVoid={() => onSetInvoiceStatus('void')}
            onRestore={() => onSetArchived(false)}
            onArchive={() => onSetArchived(true)}
            onReturnToDraft={() => onSetInvoiceStatus('draft')}
          />
        {/if}
      </svelte:fragment>
    </SectionHeading>

    {#if invoice.archivedAt}
      <NoticeBanner>This invoice is archived from active listings but still accepts contributions.</NoticeBanner>
    {:else if !isInvoicePayable(invoice)}
      <NoticeBanner>This invoice is {invoice.status} and is not accepting contributions.</NoticeBanner>
    {/if}

    {#if invoice.status === 'draft' && (invoiceIssuanceIssues.length > 0 || invoiceReadinessWarnings.length > 0)}
      <NoticeBanner as="section" ariaLabel="Invoice readiness">
        <strong>Before sharing this invoice:</strong>
        <ul>
          {#each invoiceIssuanceIssues as issue}
            <li>{issue}</li>
          {/each}
          {#each invoiceReadinessWarnings as warning}
            <li>{warning}</li>
          {/each}
        </ul>
      </NoticeBanner>
    {/if}

    {#if isOperator && invoice.status !== 'void'}
      <ExpectedQuotePanel currency={invoice.currency} quote={operatorExpectedQuote} feeQuoteAvailable={operatorFeeQuoteAvailable} />
    {/if}

    {#if invoice.status === 'draft'}
      <InvoiceDraftEditor
        bind:draftTitle
        bind:draftDueDate
        bind:draftTaxPercent
        bind:draftCurrency
        currencyLocked={Boolean(invoice.issuedAt)}
        bind:draftMinimumRate
        {savingInvoiceDraft}
        {onSaveDraft}
        {editingHourBitId}
        bind:bitLabel
        bind:bitHours
        bind:bitFixedCost
        bind:bitDescription
        bind:bitReported
        {savingHourBit}
        {onSaveBit}
        onCancelEdit={onCancelEditBit}
      />
    {:else if invoice.status === 'issued'}
      <ShareInvoiceLinkPanel {payerUrl} {onCopyLink} {onShareLink} />
    {/if}

    <!-- Both lists used to run together as two unlabelled stacks of the same
         card, so there was no line at which work items stopped and payments
         began. An operator working a queue reads the boundary, not the shape. -->
    <h3 class="list-caption">Work items</h3>
    <div class="bit-list">
      {#each hourBits as bit (bit._id)}
        <OperatorHourBitCard
          {bit}
          currency={invoice.currency}
          rates={bitRates(bit)}
          canManage={isOperator}
          editable={invoice.status === 'draft'}
          onToggleReported={(reported) => onToggleReported(bit, reported)}
          onEdit={() => onEditBit(bit)}
          onRemove={() => onRemoveBit(bit)}
        />
      {:else}
        {#if invoice.status === 'draft'}
          <!-- "above" is the draft editor, which only renders on a draft. -->
          <p class="quiet">No work reported yet — add your first hours above.</p>
        {:else}
          <p class="quiet">No work is recorded on this invoice.</p>
        {/if}
      {/each}
    </div>
    <!-- A draft cannot have been paid, so it gets no empty payments section. Any
         other status does, including one with no payments yet: silence there is
         ambiguous between "nobody has paid" and "this did not load". -->
    {#if isOperator && (invoice.status !== 'draft' || invoiceAttempts.length > 0)}
      <h3 class="list-caption">
        <!-- `{' '}` because Svelte trims template whitespace at the element
             boundary and the count rendered as "PAYMENTS(1)" — the same defect
             `def59ec` fixed on /intro. -->
        Payments{#if invoiceAttempts.length}<span class="quiet">{' '}({invoiceAttempts.length})</span>{/if}
      </h3>
      <div class="bit-list">
        {#each invoiceAttempts as attempt (attempt._id)}
          <PaymentAttemptCard {attempt} {attemptItems} {riskObservations} />
        {:else}
          <p class="quiet">Nobody has paid toward this yet.</p>
        {/each}
      </div>
    {/if}
  <!-- Empty is an invitation (APP_STYLE §8), but only where the invitation is
       actionable. Archived and paged-past-the-end are ordinary empty lists, not a
       first run, and offering to start an invoice from either would misread where
       the operator is. -->
  {:else if isOperator && hasNoInvoicesHere && showArchived}
    <p class="quiet">Nothing archived yet. Archived invoices keep accepting contributions.</p>
  {:else if isOperator && hasNoInvoicesHere && invoicePage > 1}
    <p class="quiet">No invoices on this page.</p>
  {:else if isOperator && hasNoInvoicesHere && canCreateInvoice}
    <div class="empty-state">
      <h2>No work reported yet</h2>
      <p class="quiet">
        Start your first invoice: say who it's for, add the work as you go, then
        review and share the link when you're ready to be paid.
      </p>
      <!-- Disabled only while the create is in flight, where the label says why.
           APP_STYLE §7 rejects a disabled control that gives no feedback. -->
      <button class="primary" disabled={creatingInvoice} type="button" on:click={onCreateInvoice}>
        {creatingInvoice ? 'Creating draft…' : 'Start your first invoice'}
      </button>
    </div>
  {:else if isOperator && hasNoInvoicesHere}
    <p class="quiet">No work reported yet. Choose a project to start an invoice in.</p>
  {:else if isOperator}
    <p class="quiet">Select an invoice from the list.</p>
  {/if}
</section>
